import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Plus } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { GlAccountUpsertDrawer, JournalPostDrawer } from '../../components/LedgerActionDrawers';
import { GlAccountDrawer, GlJournalDrawer } from '../../components/LedgerDetailDrawers';
import { useGlAccounts, useGlJournals, type GlKind } from '../../hooks/useLedger';
import { QueryError } from '../../components/ui/QueryError';
import { formatDate } from '../../lib/format';

const KIND_TONE: Record<GlKind, 'neutral' | 'accent' | 'success' | 'warn' | 'muted'> = {
  asset: 'accent',
  liability: 'warn',
  equity: 'neutral',
  revenue: 'success',
  expense: 'muted',
};

export function LedgerPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [postJournalOpen, setPostJournalOpen] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [journalId, setJournalId] = useState<string | null>(null);
  const accounts = useGlAccounts(wsId);
  const journals = useGlJournals(wsId);

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="display text-2xl font-semibold">{t('ledger.title')}</h1>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setCreateAccountOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 text-sm font-medium"
            >
              <Plus size={14} />
              {t('ledger.addAccount')}
            </button>
            <button
              type="button"
              onClick={() => setPostJournalOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium"
            >
              <BookOpen size={14} />
              {t('ledger.postJournal')}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <Card>
            <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 font-medium">
              {t('ledger.accounts')}{' '}
              <span className="text-ink-500 text-sm">({accounts.data?.length ?? 0})</span>
            </div>
            {accounts.isLoading ? (
              <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
            ) : accounts.isError ? (
              <QueryError onRetry={accounts.refetch} />
            ) : accounts.data?.length === 0 ? (
              <div className="p-10 text-center text-ink-500 text-sm">{t('ledger.noAccounts')}</div>
            ) : (
              <ul className="divide-y divide-ink-200 dark:divide-ink-800 max-h-[28rem] overflow-y-auto">
                {accounts.data?.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => setAccountId(a.id)}
                      className="w-full px-5 py-2.5 flex items-center justify-between text-sm text-left hover:bg-ink-50 dark:hover:bg-ink-900 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs text-ink-400 shrink-0">{a.code}</span>
                        <span className={a.is_active ? '' : 'text-ink-400 line-through'}>{a.name}</span>
                      </div>
                      <Pill tone={KIND_TONE[a.kind]}>{t(`ledger.create.kinds.${a.kind}`)}</Pill>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 font-medium">
              {t('ledger.journals')}{' '}
              <span className="text-ink-500 text-sm">({journals.data?.length ?? 0})</span>
            </div>
            {journals.isLoading ? (
              <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
            ) : journals.isError ? (
              <QueryError onRetry={journals.refetch} />
            ) : journals.data?.length === 0 ? (
              <div className="p-10 text-center text-ink-500 text-sm">{t('ledger.noJournals')}</div>
            ) : (
              <ul className="divide-y divide-ink-200 dark:divide-ink-800 max-h-[28rem] overflow-y-auto">
                {journals.data?.map((j) => (
                  <li key={j.id}>
                    <button
                      type="button"
                      onClick={() => setJournalId(j.id)}
                      className="w-full px-5 py-2.5 text-sm flex items-center justify-between text-left hover:bg-ink-50 dark:hover:bg-ink-900 transition-colors"
                    >
                      <div>
                        <div className="font-medium">{j.memo ?? j.kind}</div>
                        <div className="text-xs text-ink-500">
                          {formatDate(j.posted_on, i18n.language)}
                          {j.reversed_by && (
                            <span className="ml-2 text-warn-600">· {t('ledger.reversed')}</span>
                          )}
                        </div>
                      </div>
                      <Pill tone="neutral">{j.kind}</Pill>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {wsId && (
        <>
          <GlAccountUpsertDrawer
            wsId={wsId}
            open={createAccountOpen}
            onOpenChange={setCreateAccountOpen}
          />
          <JournalPostDrawer
            wsId={wsId}
            open={postJournalOpen}
            onOpenChange={setPostJournalOpen}
          />
          <GlAccountDrawer
            wsId={wsId}
            accountId={accountId}
            open={!!accountId}
            onOpenChange={(o) => !o && setAccountId(null)}
          />
          <GlJournalDrawer
            wsId={wsId}
            journalId={journalId}
            open={!!journalId}
            onOpenChange={(o) => !o && setJournalId(null)}
          />
        </>
      )}
    </AppShell>
  );
}
